import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Button, Input } from '../atoms';
import { Modal } from './Modal';
import { useToast } from './Toast';

/**
 * ManageParentMembersModal — RH§2 "add clients to a parent" as a shared modal.
 *
 * A SEARCHABLE (not scroll) multi-select of the clients that can be added to a
 * given parent: unassigned clients only (a client already under another parent
 * isn't listed — the one-parent rule). Adds them all in one pass via
 * POST /api/parent-clients/:parentId/members.
 *
 * Reused by BOTH the parent-client profile roll-up (ParentClientRollup) and the
 * parent-clients LIST view (ParentClientsList) so the two surfaces add members
 * identically (reviewer ask 2026-07-24 — the list view was missing this).
 */

export interface AssignableClient {
  id: string;
  name: string;
  short_name: string | null;
  parent_client_id: string | null;
}

export function ManageParentMembersModal({
  parentId,
  parentName,
  open,
  onClose,
  onAdded,
}: {
  parentId: string;
  /** Shown in the modal title so, from the list view, the user knows which parent. */
  parentName?: string;
  open: boolean;
  onClose: () => void;
  /** Called after a successful add (before onClose) — lets a caller refetch counts. */
  onAdded?: () => void;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Reset selection + search when the modal closes, so reopening starts clean
  // (the modal may be mounted persistently by its host).
  useEffect(() => { if (!open) { setSelected(new Set()); setQuery(''); } }, [open]);

  const assignableQuery = useQuery<AssignableClient[]>({
    queryKey: ['assignable-clients', parentId],
    queryFn: ({ signal }) => api.get<AssignableClient[]>(`/api/parent-clients/${parentId}/assignable-clients`, signal),
    enabled: open,
  });
  // Only truly-unassigned clients are addable (the endpoint also returns current
  // members so it can back other views; here we want the ones NOT yet in a parent).
  const addable = (assignableQuery.data ?? []).filter((c) => !c.parent_client_id);
  const q = query.trim().toLowerCase();
  const filtered = q ? addable.filter((c) => c.name.toLowerCase().includes(q) || (c.short_name ?? '').toLowerCase().includes(q)) : addable;

  const addMutation = useMutation<{ attached: string[]; skipped: unknown[] }, Error, void>({
    mutationFn: () => api.post<{ attached: string[]; skipped: unknown[] }>(`/api/parent-clients/${parentId}/members`, { client_ids: [...selected] }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['parent-client-rollup', parentId] });
      qc.invalidateQueries({ queryKey: ['assignable-clients', parentId] });
      setSelected(new Set()); setQuery('');
      toast({ variant: 'success', message: `Added ${res.attached?.length ?? 0} client(s) to this parent.` });
      onAdded?.();
      onClose();
    },
    onError: (err) => toast({ variant: 'error', message: err instanceof ApiClientError ? err.message : 'Could not add the clients.' }),
  });

  const toggle = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <Modal
      open={open}
      onClose={() => { if (!addMutation.isPending) onClose(); }}
      title={parentName ? `Add clients to ${parentName}` : 'Add clients to this parent'}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={addMutation.isPending}>Cancel</Button>
          <Button variant="primary" loading={addMutation.isPending} disabled={selected.size === 0} onClick={() => addMutation.mutate()}>
            Add {selected.size > 0 ? `${selected.size} ` : ''}client{selected.size === 1 ? '' : 's'}
          </Button>
        </>
      }
    >
      <Input placeholder="Search businesses…" value={query} onChange={(e) => setQuery(e.target.value)} style={{ marginBottom: 'var(--sp-3)' }} />
      <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)' }}>
        {assignableQuery.isLoading ? (
          <p style={{ padding: 'var(--sp-3)', color: 'var(--color-ink-3)' }}>Loading businesses…</p>
        ) : assignableQuery.isError ? (
          <p style={{ padding: 'var(--sp-3)', color: 'var(--color-status-red)' }}>
            Couldn't load clients — {(assignableQuery.error as Error)?.message || 'please try again'}.
          </p>
        ) : filtered.length === 0 ? (
          <p style={{ padding: 'var(--sp-3)', color: 'var(--color-ink-3)' }}>
            {addable.length === 0 ? 'No unassigned clients to add — every client is already under a parent.' : 'No clients match that search.'}
          </p>
        ) : (
          filtered.map((c) => (
            <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', padding: 'var(--sp-2) var(--sp-3)', cursor: 'pointer', borderBottom: '1px solid var(--color-border)' }}>
              <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} />
              <span style={{ color: 'var(--color-ink)' }}>{c.name}</span>
              {c.short_name && c.short_name !== c.name && <span style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-xs)' }}>· {c.short_name}</span>}
            </label>
          ))
        )}
      </div>
    </Modal>
  );
}
